import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Pagination } from '../components/Pagination';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AsyncSearchSelect } from '../components/AsyncSearchSelect';

// Opportunity stages with colored top bars (matching Leads Kanban style)
const OPP_STAGES = [
  { id: 'qualification', label: 'Qualification', color: '#3B82F6', bgColor: 'bg-blue-500' },
  { id: 'discovery', label: 'Discovery', color: '#06B6D4', bgColor: 'bg-cyan-500' },
  { id: 'meeting', label: 'Meeting', color: '#8B5CF6', bgColor: 'bg-purple-500' },
  { id: 'proposal', label: 'Proposal', color: '#F59E0B', bgColor: 'bg-amber-500' },
  { id: 'negotiation', label: 'Negotiation', color: '#F97316', bgColor: 'bg-orange-500' },
  { id: 'closed_won', label: 'Won', color: '#10B981', bgColor: 'bg-emerald-500' },
  { id: 'closed_lost', label: 'Lost', color: '#6B7280', bgColor: 'bg-gray-500' },
];

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/50 dark:text-orange-300' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-300' },
};

const SOURCE_COLORS = {
  lead_conversion: 'bg-blue-400',
  direct: 'bg-green-400',
  upsell: 'bg-purple-400',
  renewal: 'bg-teal-400',
  referral: 'bg-amber-400',
  email: 'bg-indigo-400',
  import: 'bg-slate-400',
};

const InfoIcon = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const formatCurrency = (value) => {
  if (!value) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
};

// KPI Card - matching Leads page exactly
const KPICard = ({ title, value, trend, subValue }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {title}<InfoIcon />
      </span>
      <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</span>
      {trend !== undefined && (
        <span className={`inline-flex items-center text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
            {trend >= 0 ? (
              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            ) : (
              <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
            )}
          </svg>
          {Math.abs(trend)}
        </span>
      )}
    </div>
    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subValue}</p>
  </div>
);

// Opportunity Card for Kanban
function OppCard({ opp, isDragging, onClick }) {
  const sourceColor = SOURCE_COLORS[opp.source] || SOURCE_COLORS.direct;
  const priorityConfig = PRIORITY_CONFIG[opp.priority] || PRIORITY_CONFIG.medium;

  return (
    <div
      onClick={() => onClick(opp)}
      className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 cursor-pointer transition-all duration-200 group
        ${isDragging ? 'shadow-xl scale-105 rotate-1 border-blue-400' : 'shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600'}
      `}
      data-testid={`opp-card-${opp.id}`}
    >
      {/* Source Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${sourceColor}`} title={opp.source || 'Direct'} />
          <span className="text-[10px] text-slate-400 dark:text-slate-500 uppercase">{(opp.source || 'direct').replace('_', ' ')}</span>
        </div>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${priorityConfig.color}`}>
          {priorityConfig.label}
        </span>
      </div>

      {/* Name */}
      <h4 className="font-semibold text-slate-900 dark:text-white text-sm leading-tight mb-1 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {opp.name}
      </h4>

      {/* Company / Lead */}
      <p className="text-xs text-slate-500 dark:text-slate-400 truncate mb-2">
        {opp.company_name || opp.lead_name || 'No company'}
      </p>

      {/* Probability Bar */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${opp.probability >= 70 ? 'bg-emerald-500' : opp.probability >= 40 ? 'bg-amber-500' : 'bg-slate-400'}`}
            style={{ width: `${opp.probability}%` }}
          />
        </div>
        <span className="text-[10px] text-slate-400 font-medium">{opp.probability}%</span>
      </div>

      {/* Products Badge */}
      {opp.linked_products?.length > 0 && (
        <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-teal-50 dark:bg-teal-900/20 rounded">
          <svg className="w-3 h-3 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <span className="text-[10px] font-medium text-teal-700 dark:text-teal-300">{opp.linked_products.length} product{opp.linked_products.length > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Bottom Row */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
        {opp.expected_close_date ? (
          <span className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {new Date(opp.expected_close_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
        ) : <span />}
        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
          {formatCurrency(opp.deal_value)}
        </span>
      </div>

      {/* NAD Indicator */}
      {opp.next_action_date && (
        (() => {
          const nadDate = new Date(opp.next_action_date);
          const today = new Date(); today.setHours(0,0,0,0);
          const diffDays = Math.floor((nadDate - today) / (1000*60*60*24));
          const isOverdue = diffDays < 0;
          const isToday = diffDays === 0;
          const color = isOverdue ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
            : isToday ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800';
          return (
            <div className={`mt-1.5 px-2 py-1 rounded border text-[10px] font-medium flex items-center gap-1 ${color}`} data-testid={`nad-opp-${opp.id}`}>
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              <span>NAD: {nadDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
              {isOverdue && <span className="font-bold">({Math.abs(diffDays)}d overdue)</span>}
              {isToday && <span className="font-bold">(Today)</span>}
            </div>
          );
        })()
      )}

      {/* Owner */}
      {opp.user_name && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
          <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 text-[10px] font-semibold">
            {opp.user_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{opp.user_name}</span>
        </div>
      )}
    </div>
  );
}

// Sortable Wrapper
function SortableOppCard({ opp, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: opp.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, zIndex: isDragging ? 1000 : 1 };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="touch-none cursor-grab active:cursor-grabbing">
      <OppCard opp={opp} isDragging={isDragging} onClick={onClick} />
    </div>
  );
}

// Column Component
function OppColumn({ stage, opps, onOppClick, isOver }) {
  const { setNodeRef } = useDroppable({ id: stage.id });
  const oppIds = useMemo(() => opps.map(o => o.id), [opps]);
  const totalValue = opps.reduce((sum, o) => sum + (o.deal_value || 0), 0);

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 flex flex-col bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 transition-all duration-200 ${isOver ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
      data-testid={`opp-column-${stage.id}`}
    >
      <div className={`h-1.5 ${stage.bgColor}`} />
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">{stage.label}</h3>
            <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">{opps.length}</span>
          </div>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{formatCurrency(totalValue)}</p>
      </div>

      <div className="flex-1 p-2 overflow-y-auto min-h-[300px] max-h-[calc(100vh-380px)] space-y-2 bg-white dark:bg-slate-800">
        <SortableContext items={oppIds} strategy={verticalListSortingStrategy}>
          {opps.map(opp => (
            <SortableOppCard key={opp.id} opp={opp} onClick={onOppClick} />
          ))}
        </SortableContext>
        {opps.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-slate-400 dark:text-slate-500 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
            <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="text-xs">Drop deals here</span>
          </div>
        )}
      </div>
    </div>
  );
}

// List View Component
function OppsListView({ opportunities, onOppClick, stages, formatCurrency, selectedIds, onToggleSelect, onToggleSelectAll, onRequestDelete }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortField, setSortField] = useState('deal_value');
  const [sortOrder, setSortOrder] = useState('desc');

  const sortedOpps = useMemo(() => {
    return [...opportunities].sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (sortField === 'deal_value') { aVal = aVal || 0; bVal = bVal || 0; }
      if (sortField === 'expected_close_date') { aVal = aVal || ''; bVal = bVal || ''; }
      if (typeof aVal === 'string') return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [opportunities, sortField, sortOrder]);

  const totalPages = Math.ceil(sortedOpps.length / itemsPerPage);
  const paginatedOpps = sortedOpps.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => (
    <svg className={`w-3 h-3 ml-1 inline ${sortField === field ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sortField === field && sortOrder === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
    </svg>
  );

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="opps-list-view">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={paginatedOpps.length > 0 && paginatedOpps.every(o => selectedIds?.has(o.id))}
                  onChange={() => onToggleSelectAll && onToggleSelectAll(paginatedOpps.map(o => o.id))}
                  className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                  data-testid="select-all-opps"
                />
              </th>
              <th onClick={() => toggleSort('name')} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-slate-700 dark:hover:text-slate-200">Name<SortIcon field="name" /></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Stage</th>
              <th onClick={() => toggleSort('deal_value')} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-slate-700 dark:hover:text-slate-200">Value<SortIcon field="deal_value" /></th>
              <th onClick={() => toggleSort('probability')} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-slate-700 dark:hover:text-slate-200">Probability<SortIcon field="probability" /></th>
              <th onClick={() => toggleSort('expected_close_date')} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-slate-700 dark:hover:text-slate-200">Close Date<SortIcon field="expected_close_date" /></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Priority</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginatedOpps.map(opp => {
              const stageInfo = stages.find(s => s.id === opp.stage) || stages[0];
              const priorityConfig = PRIORITY_CONFIG[opp.priority] || PRIORITY_CONFIG.medium;
              const isSelected = selectedIds?.has(opp.id);
              return (
                <tr
                  key={opp.id}
                  onClick={() => onOppClick(opp)}
                  className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                  data-testid={`opp-row-${opp.id}`}
                >
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!isSelected}
                      onChange={() => onToggleSelect && onToggleSelect(opp.id)}
                      className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                      data-testid={`select-opp-${opp.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white text-sm">{opp.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{opp.company_name || opp.lead_name || '-'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${stageInfo.color}15`, color: stageInfo.color }}>
                      {stageInfo.label}
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className="text-sm font-semibold text-slate-900 dark:text-white">{formatCurrency(opp.deal_value)}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${opp.probability >= 70 ? 'bg-emerald-500' : opp.probability >= 40 ? 'bg-amber-500' : 'bg-slate-400'}`} style={{ width: `${opp.probability}%` }} />
                      </div>
                      <span className="text-xs text-slate-600 dark:text-slate-300">{opp.probability}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {opp.expected_close_date ? new Date(opp.expected_close_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${priorityConfig.color}`}>{priorityConfig.label}</span></td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to={`/opportunities/${opp.id}`}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded transition-colors"
                        title="View"
                        data-testid={`view-opp-${opp.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => onRequestDelete && onRequestDelete(opp.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                        title="Delete"
                        data-testid={`delete-opp-${opp.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {paginatedOpps.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400 dark:text-slate-500">No opportunities found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedOpps.length)} of {sortedOpps.length}</span>
            <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {[10, 25, 50].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">First</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">Last</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Component
export default function OpportunitiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban');
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [oppPage, setOppPage] = useState(1);
  const [oppLimit, setOppLimit] = useState(20);
  const [oppPagination, setOppPagination] = useState(null);
  const [listOpps, setListOpps] = useState([]);
  // Bulk-delete state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // SDF (Sales Deal Form) state for drag-to-close on Kanban
  const [sdfModal, setSdfModal] = useState(null); // { oppId, targetStage: 'closed_won'|'closed_lost', prevStage }
  const [sdfForm, setSdfForm] = useState({
    win_reason: '', competitor: '', decision_makers: '', loss_reason: '', close_notes: ''
  });
  const [sdfSaving, setSdfSaving] = useState(false);
  const [sdfErr, setSdfErr] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/opportunities`, { params: { include_closed: true } });
      setOpportunities(res.data);
    } catch (err) {
      console.error('Error fetching opportunities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Paginated fetch for list view
  const fetchListOpps = useCallback(async () => {
    if (viewMode !== 'list') return;
    try {
      const params = { page: oppPage, limit: oppLimit, include_closed: true };
      if (searchQuery) params.search = searchQuery;
      if (stageFilter && stageFilter !== 'all') params.stage = stageFilter;
      const res = await axios.get(`${API}/opportunities`, { params });
      if (res.data?.pagination) {
        setListOpps(res.data.data || []);
        setOppPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching paginated opps:', err);
    }
  }, [viewMode, oppPage, oppLimit, searchQuery, stageFilter]);

  useEffect(() => { fetchListOpps(); }, [fetchListOpps]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/opportunities/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
    fetchStats();
  }, [fetchOpportunities, fetchStats, location.state?.refresh]);

  const handleStageChange = async (oppId, newStage, extras = null) => {
    try {
      const body = { stage: newStage, ...(extras || {}) };
      await axios.post(`${API}/opportunities/${oppId}/stage`, body);
      fetchOpportunities();
      fetchStats();
      return { ok: true };
    } catch (err) {
      console.error('Failed to update stage:', err);
      const msg = err?.response?.data?.detail || 'Failed to update stage';
      return { ok: false, error: msg };
    }
  };

  // Drag handlers
  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragOver = (event) => setOverId(event.over?.id || null);
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    if (!over) return;

    const oppId = active.id;
    const targetStageId = over.id;

    // Check if dropped on a column
    if (OPP_STAGES.some(s => s.id === targetStageId)) {
      const opp = opportunities.find(o => o.id === oppId);
      if (opp && opp.stage !== targetStageId) {
        // Closing stages require the Sales Deal Form — open modal instead of direct update
        if (targetStageId === 'closed_won' || targetStageId === 'closed_lost') {
          setSdfModal({ oppId, targetStage: targetStageId, prevStage: opp.stage, oppName: opp.name });
          setSdfForm({ win_reason: '', competitor: '', decision_makers: '', loss_reason: '', close_notes: '' });
          setSdfErr('');
          return;
        }
        // Optimistic update for non-closing stages
        setOpportunities(prev => prev.map(o => o.id === oppId ? { ...o, stage: targetStageId } : o));
        handleStageChange(oppId, targetStageId);
      }
    }
  };

  const submitSdfClose = async () => {
    if (!sdfModal) return;
    const { oppId, targetStage } = sdfModal;
    setSdfSaving(true);
    setSdfErr('');
    let payload = {};
    if (targetStage === 'closed_won') {
      const dms = (sdfForm.decision_makers || '').split(',').map(s => s.trim()).filter(Boolean);
      if (!sdfForm.win_reason.trim()) { setSdfErr('Win reason is required'); setSdfSaving(false); return; }
      if (!sdfForm.competitor.trim()) { setSdfErr('Competitor is required (enter "None" if none)'); setSdfSaving(false); return; }
      if (dms.length === 0) { setSdfErr('At least one decision maker is required'); setSdfSaving(false); return; }
      payload = {
        win_reason: sdfForm.win_reason.trim(),
        competitor: sdfForm.competitor.trim(),
        decision_makers: dms,
        close_notes: sdfForm.close_notes.trim() || undefined,
      };
    } else {
      if (!sdfForm.loss_reason.trim()) { setSdfErr('Loss reason is required'); setSdfSaving(false); return; }
      payload = {
        loss_reason: sdfForm.loss_reason.trim(),
        competitor: sdfForm.competitor.trim() || undefined,
        close_notes: sdfForm.close_notes.trim() || undefined,
      };
    }
    const result = await handleStageChange(oppId, targetStage, payload);
    setSdfSaving(false);
    if (result.ok) {
      setSdfModal(null);
    } else {
      setSdfErr(result.error);
    }
  };

  const handleOppClick = (opp) => navigate(`/opportunities/${opp.id}`);

  const filteredOpps = useMemo(() => {
    return opportunities.filter(opp => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!opp.name?.toLowerCase().includes(q) && !opp.company_name?.toLowerCase().includes(q) && !opp.lead_name?.toLowerCase().includes(q)) return false;
      }
      if (stageFilter !== 'all' && opp.stage !== stageFilter) return false;
      if (priorityFilter !== 'all' && opp.priority !== priorityFilter) return false;
      return true;
    });
  }, [opportunities, searchQuery, stageFilter, priorityFilter]);

  const oppsByStage = useMemo(() => {
    const grouped = {};
    OPP_STAGES.forEach(stage => { grouped[stage.id] = []; });
    filteredOpps.forEach(opp => {
      if (grouped[opp.stage]) grouped[opp.stage].push(opp);
      else if (grouped.qualification) grouped.qualification.push(opp);
    });
    return grouped;
  }, [filteredOpps]);

  const activeOpp = activeId ? opportunities.find(o => o.id === activeId) : null;

  // Stats
  const openPipeline = opportunities.filter(o => !['closed_won', 'closed_lost'].includes(o.stage)).reduce((s, o) => s + (o.deal_value || 0), 0);
  const wonCount = opportunities.filter(o => o.stage === 'closed_won').length;
  const totalClosed = opportunities.filter(o => ['closed_won', 'closed_lost'].includes(o.stage)).length;
  const winRate = totalClosed > 0 ? ((wonCount / totalClosed) * 100).toFixed(1) : '0.0';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]" data-testid="opportunities-page">
      {/* Header */}
      <div className="flex-shrink-0 px-4 lg:px-6 pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Opportunities</h1>
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-0.5">
              <button onClick={() => setViewMode('kanban')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'kanban' ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-testid="kanban-view-btn">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>
                Kanban
              </button>
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-testid="list-view-btn">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                List
              </button>
            </div>
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors text-sm font-medium" data-testid="add-opportunity-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add Opportunity
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KPICard title="OPEN PIPELINE" value={formatCurrency(openPipeline)} trend={0} subValue="in pipeline" />
          <KPICard title="WON THIS MONTH" value={formatCurrency(stats?.won_value_this_month || 0)} trend={0} subValue="deals won" />
          <KPICard title="WIN RATE" value={`${winRate}%`} trend={0} subValue="conversion" />
          <KPICard title="TOTAL DEALS" value={(stats?.total_opportunities ?? oppPagination?.total ?? opportunities.length).toLocaleString()} trend={0} subValue="opportunities" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Search opportunities..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400" data-testid="opp-search" />
          </div>
          <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300" data-testid="opp-stage-filter">
            <option value="all">All Stages</option>
            {OPP_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300" data-testid="opp-priority-filter">
            <option value="all">All Priorities</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'kanban' ? (
        <div className="flex-1 overflow-x-auto overflow-y-hidden px-4 lg:px-6 py-4">
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
            <div className="flex gap-4 h-full">
              {OPP_STAGES.map(stage => (
                <OppColumn key={stage.id} stage={stage} opps={oppsByStage[stage.id] || []} onOppClick={handleOppClick} isOver={overId === stage.id} />
              ))}
            </div>
            <DragOverlay>
              {activeOpp && <div className="rotate-3"><OppCard opp={activeOpp} isDragging={true} onClick={() => {}} /></div>}
            </DragOverlay>
          </DndContext>
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-4 lg:p-6">
          {selectedIds.size > 0 && (
            <div className="mb-3 flex items-center gap-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-testid="opp-bulk-toolbar">
              <span className="text-sm text-slate-700 dark:text-slate-200 font-medium" data-testid="bulk-selection-count">
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => setBulkDeleteOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                data-testid="bulk-delete-btn"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete {selectedIds.size} selected
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                data-testid="bulk-clear-btn"
              >
                Clear
              </button>
            </div>
          )}
          <OppsListView
            opportunities={oppPagination ? listOpps : filteredOpps}
            onOppClick={handleOppClick}
            stages={OPP_STAGES}
            formatCurrency={formatCurrency}
            selectedIds={selectedIds}
            onToggleSelect={(id) => setSelectedIds(prev => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            })}
            onToggleSelectAll={(ids) => setSelectedIds(prev => {
              const next = new Set(prev);
              const allSelected = ids.every(id => next.has(id));
              if (allSelected) ids.forEach(id => next.delete(id));
              else ids.forEach(id => next.add(id));
              return next;
            })}
            onRequestDelete={(id) => { setSelectedIds(new Set([id])); setBulkDeleteOpen(true); }}
          />
          {oppPagination && (
            <Pagination
              pagination={oppPagination}
              onPageChange={(p) => setOppPage(p)}
              onLimitChange={(l) => { setOppLimit(l); setOppPage(1); }}
            />
          )}
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      {bulkDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="bulk-delete-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Delete {selectedIds.size} opportunit{selectedIds.size === 1 ? 'y' : 'ies'}?</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              This permanently removes the selected opportunit{selectedIds.size === 1 ? 'y' : 'ies'}. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                data-testid="bulk-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedIds.size === 0) return;
                  try {
                    setBulkDeleting(true);
                    const ids = Array.from(selectedIds);
                    await axios.post(`${API}/opportunities/bulk-delete`, { ids });
                    setSelectedIds(new Set());
                    setBulkDeleteOpen(false);
                    fetchOpportunities();
                    fetchStats();
                  } catch (err) {
                    console.error('Bulk delete failed', err);
                  } finally {
                    setBulkDeleting(false);
                  }
                }}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg"
                data-testid="bulk-delete-confirm"
              >
                {bulkDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddOpportunityModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); fetchOpportunities(); fetchStats(); }}
        />
      )}

      {/* Sales Deal Form (SDF) Modal — shown on drag-to-close */}
      {sdfModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="kanban-sdf-modal"
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
              {sdfModal.targetStage === 'closed_won' ? 'Mark as Won' : 'Mark as Lost'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              Sales Deal Form — {sdfModal.oppName || 'Opportunity'}
            </p>

            {sdfModal.targetStage === 'closed_won' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Why did we win? <span className="text-red-500">*</span></label>
                  <textarea
                    rows={2}
                    value={sdfForm.win_reason}
                    onChange={(e) => { setSdfForm(f => ({ ...f, win_reason: e.target.value })); setSdfErr(''); }}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                    data-testid="kanban-sdf-win-reason"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Competitor <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={sdfForm.competitor}
                    onChange={(e) => { setSdfForm(f => ({ ...f, competitor: e.target.value })); setSdfErr(''); }}
                    placeholder='e.g. Salesforce, or "None"'
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                    data-testid="kanban-sdf-competitor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Decision Makers <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={sdfForm.decision_makers}
                    onChange={(e) => { setSdfForm(f => ({ ...f, decision_makers: e.target.value })); setSdfErr(''); }}
                    placeholder="Comma-separated — e.g. Priya Sharma, Arjun Mehta"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                    data-testid="kanban-sdf-decision-makers"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Close Notes <span className="text-slate-400">(optional)</span></label>
                  <textarea
                    rows={2}
                    value={sdfForm.close_notes}
                    onChange={(e) => setSdfForm(f => ({ ...f, close_notes: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                    data-testid="kanban-sdf-close-notes"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Loss Reason <span className="text-red-500">*</span></label>
                  <textarea
                    rows={2}
                    value={sdfForm.loss_reason}
                    onChange={(e) => { setSdfForm(f => ({ ...f, loss_reason: e.target.value })); setSdfErr(''); }}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                    data-testid="kanban-sdf-loss-reason"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Competitor <span className="text-slate-400">(if lost to one)</span></label>
                  <input
                    type="text"
                    value={sdfForm.competitor}
                    onChange={(e) => setSdfForm(f => ({ ...f, competitor: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                    data-testid="kanban-sdf-lost-competitor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Close Notes <span className="text-slate-400">(optional)</span></label>
                  <textarea
                    rows={2}
                    value={sdfForm.close_notes}
                    onChange={(e) => setSdfForm(f => ({ ...f, close_notes: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                    data-testid="kanban-sdf-lost-close-notes"
                  />
                </div>
              </div>
            )}

            {sdfErr && (
              <div className="mt-3 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm" data-testid="kanban-sdf-error">
                {sdfErr}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setSdfModal(null)}
                disabled={sdfSaving}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                data-testid="kanban-sdf-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitSdfClose}
                disabled={sdfSaving}
                className={`px-4 py-2 rounded-lg text-white ${
                  sdfModal.targetStage === 'closed_won'
                    ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-400'
                    : 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                }`}
                data-testid="kanban-sdf-submit"
              >
                {sdfSaving ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}

// Add Opportunity Modal
function AddOpportunityModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '', lead_id: '', lead_label: '', company_id: '', company_label: '', deal_value: '', stage: 'qualification',
    probability: 10, expected_close_date: '', priority: 'medium', description: '', next_step: '',
    next_action_date: '', next_action_note: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then(res => {
      setAllProducts((res.data || []).filter(p => p.active !== false));
    }).catch(() => {});
  }, []);

  // Auto-generate opportunity name: "{Company} with {Product(s)}"
  const generateOppName = (companyName, productIds) => {
    if (!companyName) return '';
    const productNames = productIds.map(pid => allProducts.find(p => p.id === pid)?.name).filter(Boolean);
    if (productNames.length === 0) return companyName;
    return `${companyName} with ${productNames.join(', ')}`;
  };

  // Update name when products or company changes
  useEffect(() => {
    const name = generateOppName(formData.company_label, selectedProducts);
    if (name) setFormData(prev => ({ ...prev, name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts, formData.company_label]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (!formData.lead_id) {
        setError('A Lead is required. Opportunities can only be created by converting a qualified lead.');
        setSaving(false);
        return;
      }
      // Iteration 74: atomic Lead → Opportunity conversion.
      await axios.post(`${API}/leads/${formData.lead_id}/convert`, {
        name: formData.name,
        deal_value: parseFloat(formData.deal_value) || 0,
        stage: formData.stage,
        expected_close_date: formData.expected_close_date || null,
        priority: formData.priority,
        description: formData.description,
        next_step: formData.next_step,
      });
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to convert lead');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Async search fetchers
  const fetchLeadOptions = async (q) => {
    try {
      const res = await axios.get(`${API}/leads`, { params: { search: q, page: 1, limit: 20 } });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      return data.map(l => ({ ...l, _label: `${l.name}${l.organization ? ` (${l.organization})` : ''}${l.email ? ` — ${l.email}` : ''}` }));
    } catch { return []; }
  };

  const fetchCompanyOptions = async (q) => {
    try {
      const res = await axios.get(`${API}/companies`, { params: { search: q, page: 1, limit: 20 } });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      return data.map(c => ({ ...c, _label: `${c.name}${c.industry ? ` — ${c.industry}` : ''}${c.city ? ` (${c.city})` : ''}` }));
    } catch { return []; }
  };

  const handleLeadSelect = async (lead) => {
    if (!lead) {
      setFormData(prev => ({ ...prev, lead_id: '', lead_label: '' }));
      return;
    }
    const updates = { lead_id: lead.id, lead_label: lead._label || lead.name };
    if (lead.company_id && !formData.company_id) {
      // Try to get company name (prefer lead.organization; otherwise fetch)
      let companyLabel = lead.organization || '';
      if (!companyLabel) {
        try {
          const res = await axios.get(`${API}/companies/${lead.company_id}`);
          companyLabel = res.data?.name || '';
        } catch {}
      }
      updates.company_id = lead.company_id;
      updates.company_label = companyLabel;
    } else if (lead.organization && !formData.company_id) {
      // No company link but we have org name — keep name generation working
      updates.company_label = lead.organization;
    }
    if (lead.deal_value && !formData.deal_value) {
      updates.deal_value = String(lead.deal_value);
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleCompanySelect = (company) => {
    if (!company) {
      setFormData(prev => ({ ...prev, company_id: '', company_label: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, company_id: company.id, company_label: company._label || company.name }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-opp-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Add Opportunity</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Opportunity Name *</label>
            <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} required placeholder="e.g., TechCorp - Contact Center License" className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400" data-testid="opp-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Link to Lead <span className="text-red-500">*</span></label>
              <AsyncSearchSelect
                value={formData.lead_id}
                valueLabel={formData.lead_label}
                onSelect={handleLeadSelect}
                fetchOptions={fetchLeadOptions}
                placeholder="Search qualified leads…"
                testId="opp-lead-search"
                renderOption={(l) => (
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 dark:text-white truncate">
                      {l.name} <span className="text-[10px] text-slate-400 uppercase ml-1">{l.status}</span>
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {l.organization ? `${l.organization}` : ''}
                      {l.organization && l.email ? ' • ' : ''}
                      {l.email || ''}
                    </span>
                  </div>
                )}
              />
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                Opportunities can only be created by converting a <b>qualified</b> lead.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company</label>
              <AsyncSearchSelect
                value={formData.company_id}
                valueLabel={formData.company_label}
                onSelect={handleCompanySelect}
                fetchOptions={fetchCompanyOptions}
                placeholder="Search companies by name, industry, city…"
                testId="opp-company-search"
                renderOption={(c) => (
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 dark:text-white truncate">{c.name}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {c.industry ? `${c.industry}` : ''}
                      {c.industry && c.city ? ' • ' : ''}
                      {c.city || ''}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Deal Value (INR) *</label>
              <input type="number" value={formData.deal_value} onChange={e => setFormData(p => ({ ...p, deal_value: e.target.value }))} required placeholder="500000" className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400" data-testid="opp-value-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Stage</label>
              <select value={formData.stage} onChange={e => { const s = e.target.value; const probs = { qualification: 10, discovery: 20, meeting: 40, proposal: 60, negotiation: 80 }; setFormData(p => ({ ...p, stage: s, probability: probs[s] || p.probability })); }} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="opp-stage-select">
                <option value="qualification">Qualification</option>
                <option value="discovery">Discovery</option>
                <option value="meeting">Meeting</option>
                <option value="proposal">Proposal</option>
                <option value="negotiation">Negotiation</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Probability (%)</label>
              <input type="number" min="0" max="100" value={formData.probability} onChange={e => setFormData(p => ({ ...p, probability: e.target.value }))} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="opp-probability-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Close Date</label>
              <input type="date" value={formData.expected_close_date} onChange={e => setFormData(p => ({ ...p, expected_close_date: e.target.value }))} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="opp-close-date-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Priority</label>
              <select value={formData.priority} onChange={e => setFormData(p => ({ ...p, priority: e.target.value }))} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="opp-priority-select">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Next Step</label>
            <input type="text" value={formData.next_step} onChange={e => setFormData(p => ({ ...p, next_step: e.target.value }))} placeholder="e.g., Schedule demo call" className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400" />
          </div>

          {/* NAD - Next Action Date */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">Next Action Date (NAD) *</label>
                <input type="date" value={formData.next_action_date} onChange={e => setFormData(p => ({ ...p, next_action_date: e.target.value }))} required min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg text-slate-900 dark:text-white" data-testid="opp-nad-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">Planned Action</label>
                <input type="text" value={formData.next_action_note} onChange={e => setFormData(p => ({ ...p, next_action_note: e.target.value }))} placeholder="e.g., Demo call" className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400" data-testid="opp-nad-note" />
              </div>
            </div>
          </div>

          {/* Products Selector */}
          {allProducts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Link Products</label>
              <div className="flex flex-wrap gap-2 p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900/30 max-h-36 overflow-y-auto">
                {allProducts.map(p => {
                  const isSelected = selectedProducts.includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => setSelectedProducts(prev => isSelected ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        isSelected
                          ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                          : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400'
                      }`} data-testid={`product-select-${p.id}`}>
                      {isSelected && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      {p.name}
                      {p.base_price > 0 && <span className="opacity-60">₹{p.base_price.toLocaleString('en-IN')}</span>}
                    </button>
                  );
                })}
              </div>
              {selectedProducts.length > 0 && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected</p>}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
            <button
              type="submit"
              disabled={saving || !formData.lead_id}
              title={!formData.lead_id ? 'Pick a Lead first — Opportunities can only be created by converting a qualified Lead.' : ''}
              className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
              data-testid="save-opportunity-btn"
            >
              {saving ? 'Creating...' : 'Create Opportunity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
